import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  bulkCreateStops,
  bulkDeleteBusRoutes,
  bulkDeleteStops,
  createBusRoute,
  createStop,
  deleteBusRoute,
  deleteStop,
  getBusRoutes,
  getStops,
  importBusRoutes,
  updateBusRoute,
  updateStop
} from '../utils/api';
import {
  DATA_PAGE_SIZE,
  DEFAULT_TAB,
  getRouteDisplayName,
  normalizeImportHeader,
  pathForTab,
  round6,
  routePaletteColor,
  tabForPath
} from '../utils/routeManagementHelpers';

export default function useRouteManagement() {
  const [stops, setStops] = useState([]);
  const [pendingMarker, setPendingMarker] = useState(null);
  const [stopId, setStopId] = useState('');
  const [stopName, setStopName] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [flyTarget, setFlyTarget] = useState(null);
  const [visibleStopIds, setVisibleStopIds] = useState(new Set());

  const [editingStop, setEditingStop] = useState(null);
  const [editStopId, setEditStopId] = useState('');
  const [editName, setEditName] = useState('');
  const [editPendingMarker, setEditPendingMarker] = useState(null);
  const [stopUpdateConfirm, setStopUpdateConfirm] = useState(null);

  /*
   * The active workspace tab is DERIVED from the URL, not stored alongside it.
   *
   * Each tab has its own address (/home, /stops, /map, /routes), so a screen can
   * be bookmarked, linked to, reopened in another browser tab, and navigated with
   * the browser's back and forward buttons. Holding the tab in local state as well
   * would give two sources of truth that drift apart the moment someone presses
   * Back; deriving it means they cannot.
   *
   * `setActiveTab` is kept as the name every call site already uses — it now
   * navigates instead of setting state.
   */
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = tabForPath(location.pathname) || DEFAULT_TAB;

  const setActiveTab = (tab) => {
    const target = pathForTab(tab);
    if (target !== location.pathname) navigate(target);
  };

  const [dragPendingStop, setDragPendingStop] = useState(null);
  const [viewingRouteIds, setViewingRouteIds] = useState(new Set());
  const [stopFormOpen, setStopFormOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('app.sidebar.collapsed') === '1';
    } catch {
      return false;
    }
  });

  const [routeName, setRouteName] = useState('');
  const [routeNumber, setRouteNumber] = useState('');
  const [routeDirection, setRouteDirection] = useState('');
  const [routeStops, setRouteStops] = useState([]);
  const [routeBuilderMode, setRouteBuilderMode] = useState('import');
  const [stopSearch, setStopSearch] = useState('');
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [viewingRoute, setViewingRoute] = useState(null);
  const [editingRoute, setEditingRoute] = useState(null);
  const [routeDragPending, setRouteDragPending] = useState(null);
  const [routeImportFiles, setRouteImportFiles] = useState([]);
  const [routeImporting, setRouteImporting] = useState(false);
  const [routeImportPreviews, setRouteImportPreviews] = useState([]);
  const [routeImportParsing, setRouteImportParsing] = useState(false);
  const [pendingMissingStopIds, setPendingMissingStopIds] = useState([]);
  const [routeSaveConfirm, setRouteSaveConfirm] = useState(null);

  const [coordsInput, setCoordsInput] = useState('');
  const [panelSearch, setPanelSearch] = useState('');
  const [mapStopSearch, setMapStopSearch] = useState('');
  const [mapSearchFocused, setMapSearchFocused] = useState(false);

  const [dataSubTab, setDataSubTab] = useState('stops');
  const [dataStopsFilter, setDataStopsFilter] = useState('');
  const [dataRoutesFilter, setDataRoutesFilter] = useState('');
  const [dataStopsSort, setDataStopsSort] = useState({ key: 'stopName', dir: 1 });
  const [dataRoutesSort, setDataRoutesSort] = useState({ key: 'routeName', dir: 1 });
  const [dataStopsPage, setDataStopsPage] = useState(1);
  const [dataRoutesPage, setDataRoutesPage] = useState(1);
  const [selectedDataStopIds, setSelectedDataStopIds] = useState(new Set());
  const [selectedDataRouteIds, setSelectedDataRouteIds] = useState(new Set());
  const [deletingSelection, setDeletingSelection] = useState(false);

  const [importRows, setImportRows] = useState([]);
  const [importSkipped, setImportSkipped] = useState(0);
  const [importError, setImportError] = useState('');
  const [importing, setImporting] = useState(false);

  const routeImportInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const isDraggingMarker = useRef(false);
  const stopsRef = useRef([]);
  const searchTimeout = useRef(null);
  const searchRef = useRef(null);
  const routeSearchRef = useRef(null);
  const mapSearchRef = useRef(null);

  // Deep-link edit intent passed via navigation state from the Command Center
  // (e.g. { edit: { type: 'stop' | 'route', id } }). Captured once here, then
  // stripped from history so a browser refresh lands on the Dashboard instead of
  // reopening the editor. (`location` and `navigate` are declared above, with the
  // tab derivation.)
  const editIntentRef = useRef(location.state?.edit || null);
  const editHandledRef = useRef(false);

  useEffect(() => {
    if (location.state?.edit) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);

  useEffect(() => {
    stopsRef.current = stops;
  }, [stops]);

  useEffect(() => {
    fetchStops(false);
    fetchRoutes();
  }, []);

  // Once stops load, open the requested stop in the real map editor.
  useEffect(() => {
    const intent = editIntentRef.current;
    if (!intent || editHandledRef.current || intent.type !== 'stop') return;
    const stop = stops.find((s) => s._id === intent.id);
    if (stop) {
      switchTab('stops');
      startEdit(stop);
      editHandledRef.current = true;
    }
  }, [stops]);

  // Once routes load, open the requested route in the route builder.
  useEffect(() => {
    const intent = editIntentRef.current;
    if (!intent || editHandledRef.current || intent.type !== 'route') return;
    const route = savedRoutes.find((r) => r._id === intent.id);
    if (route) {
      setActiveTab('routes');
      loadRouteForEdit(route);
      editHandledRef.current = true;
    }
  }, [savedRoutes]);

  useEffect(() => {
    return () => clearTimeout(searchTimeout.current);
  }, []);

  useEffect(() => {
    function handleDocumentMouseDown(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchResults([]);
      }
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, []);

  useEffect(() => {
    function handleDocumentMouseDown(event) {
      if (routeSearchRef.current && !routeSearchRef.current.contains(event.target)) {
        setStopSearch('');
      }
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, []);

  useEffect(() => {
    function handleDocumentMouseDown(event) {
      if (mapSearchRef.current && !mapSearchRef.current.contains(event.target)) {
        setMapSearchFocused(false);
      }
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, []);

  useEffect(() => {
    const validIds = new Set(stops.map((stop) => stop._id));

    setSelectedDataStopIds((previousSelection) => {
      const nextSelection = new Set(
        [...previousSelection].filter((id) => validIds.has(id))
      );

      return nextSelection.size === previousSelection.size
        ? previousSelection
        : nextSelection;
    });
  }, [stops]);

  useEffect(() => {
    const validIds = new Set(savedRoutes.map((route) => route._id));

    setSelectedDataRouteIds((previousSelection) => {
      const nextSelection = new Set(
        [...previousSelection].filter((id) => validIds.has(id))
      );

      return nextSelection.size === previousSelection.size
        ? previousSelection
        : nextSelection;
    });
  }, [savedRoutes]);

  async function fetchStops(initializeVisibleStops = false) {
    try {
      const response = await getStops();
      setStops(response.data);

      if (initializeVisibleStops) {
        setVisibleStopIds(new Set(response.data.map((stop) => stop._id)));
      }
    } catch {
      toast.error('Failed to load stops');
    }
  }

  async function fetchRoutes() {
    try {
      const response = await getBusRoutes();
      setSavedRoutes(response.data);
    } catch {
      // Routes are not critical for initial page load.
    }
  }

  function handleSearchInput(query) {
    setSearchQuery(query);
    clearTimeout(searchTimeout.current);

    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        const response = await axios.get(
          'https://nominatim.openstreetmap.org/search',
          {
            params: {
              q: query,
              format: 'json',
              limit: 6
            }
          }
        );

        setSearchResults(response.data);
      } catch {
        // Ignore transient geocoder errors.
      }
    }, 400);
  }

  function handleSelectResult(result) {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    setFlyTarget({ lat, lng });
    setPendingMarker({ lat, lng });
    setStopName(result.display_name.split(',')[0].trim());
    setSearchQuery(result.display_name);
    setSearchResults([]);
  }

  function handleCoordsInput(value) {
    setCoordsInput(value);
    const parts = value.trim().split(/[\s,]+/).map(Number);

    if (parts.length < 2 || parts.some((part) => Number.isNaN(part) || part === 0)) {
      return;
    }

    const [lat, lng] = parts;

    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      setFlyTarget({ lat, lng });
      setPendingMarker({ lat, lng });
    }
  }

  function handleMapClick(latlng) {
    if (activeTab !== 'stops' || isDraggingMarker.current) {
      return;
    }

    if (editingStop) {
      setEditPendingMarker({
        lat: round6(latlng.lat),
        lng: round6(latlng.lng)
      });
      return;
    }

    if (!stopFormOpen) {
      return;
    }

    setPendingMarker({
      lat: round6(latlng.lat),
      lng: round6(latlng.lng)
    });
  }

  async function handleSave() {
    if (!pendingMarker) {
      toast.error('Click on the map to place a stop pin');
      return;
    }

    if (!stopName.trim()) {
      toast.error('Enter a stop name');
      return;
    }

    setSaving(true);

    try {
      // StopID is optional — the server assigns one when omitted. A trimmed
      // value is only sent when the user opted into a custom code.
      const payload = {
        stopName: stopName.trim(),
        lat: round6(pendingMarker.lat),
        lng: round6(pendingMarker.lng)
      };
      if (stopId.trim()) {
        payload.stopId = stopId.trim();
      }

      const response = await createStop(payload);

      const assignedId = response.data?.stopId;
      toast.success(
        assignedId ? `"${stopName.trim()}" saved as ${assignedId}` : `"${stopName.trim()}" saved`
      );
      setVisibleStopIds((previousIds) => new Set([...previousIds, response.data._id]));
      setPendingMarker(null);
      setStopId('');
      setStopName('');
      setSearchQuery('');
      setCoordsInput('');
      setStopFormOpen(false);
      fetchStops();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save stop');
    }

    setSaving(false);
  }

  function startEdit(stop) {
    setDragPendingStop(null);
    setEditingStop(stop);
    setEditStopId(stop.stopId || '');
    setEditName(stop.stopName);
    setEditPendingMarker(null);
    setPendingMarker(null);
    setStopId('');
    setStopName('');
    setSearchQuery('');
    setStopFormOpen(true);
    setFlyTarget({ lat: stop.lat, lng: stop.lng });
  }

  function cancelEdit() {
    setEditingStop(null);
    setEditStopId('');
    setEditName('');
    setEditPendingMarker(null);
  }

  // Stage the edit and open a confirmation dialog. Covers both field edits and
  // drag-and-drop repositioning (which lands in editPendingMarker).
  function requestUpdateStop() {
    if (!editingStop) {
      return;
    }

    if (!editStopId.trim()) {
      toast.error('Enter a StopID');
      return;
    }

    if (!editName.trim()) {
      toast.error('Enter a stop name');
      return;
    }

    const nextLat = editPendingMarker ? editPendingMarker.lat : editingStop.lat;
    const nextLng = editPendingMarker ? editPendingMarker.lng : editingStop.lng;
    const nameChanged = editName.trim() !== (editingStop.stopName || '');
    const moved = nextLat !== editingStop.lat || nextLng !== editingStop.lng;

    if (!nameChanged && !moved) {
      toast.info('Nothing has changed yet');
      return;
    }

    setStopUpdateConfirm({
      name: editName.trim(),
      prevName: editingStop.stopName || '',
      lat: nextLat,
      lng: nextLng,
      prevLat: editingStop.lat,
      prevLng: editingStop.lng,
      nameChanged,
      moved
    });
  }

  // Cancelling discards every unsaved change: the name reverts and the pin snaps
  // back to its original position (the marker is bound to editPendingMarker).
  function cancelStopUpdateConfirm() {
    setStopUpdateConfirm(null);
    if (editingStop) {
      setEditName(editingStop.stopName || '');
      setEditStopId(editingStop.stopId || '');
      setEditPendingMarker(null);
    }
  }

  // Commit the confirmed stop edit. Saves to the DB (and records a version).
  async function handleUpdate() {
    if (!editingStop || !stopUpdateConfirm) {
      return;
    }

    setSaving(true);

    try {
      await updateStop(editingStop._id, {
        stopId: editStopId.trim(),
        stopName: stopUpdateConfirm.name,
        lat: stopUpdateConfirm.lat,
        lng: stopUpdateConfirm.lng
      });

      toast.success(`"${stopUpdateConfirm.name}" updated`);
      cancelEdit();
      setStopFormOpen(false);
      setStopUpdateConfirm(null);
      fetchStops();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update stop');
    }

    setSaving(false);
  }

  function toggleStopVisibility(id) {
    setVisibleStopIds((previousIds) => {
      const nextIds = new Set(previousIds);

      if (nextIds.has(id)) {
        nextIds.delete(id);
      } else {
        nextIds.add(id);
      }

      return nextIds;
    });
  }

  function removeStopsFromState(ids) {
    const idsToRemove = new Set(ids);

    setStops((previousStops) =>
      previousStops.filter((stop) => !idsToRemove.has(stop._id))
    );
    setVisibleStopIds(
      (previousIds) => new Set([...previousIds].filter((id) => !idsToRemove.has(id)))
    );
    setSelectedDataStopIds(
      (previousIds) => new Set([...previousIds].filter((id) => !idsToRemove.has(id)))
    );

    if (editingStop?._id && idsToRemove.has(editingStop._id)) {
      cancelEdit();
    }
  }

  async function handleDeleteStop(id, name) {
    if (!window.confirm(`Delete "${name}"?`)) {
      return;
    }

    try {
      await deleteStop(id);
      removeStopsFromState([id]);
      toast.success('Stop deleted');
    } catch {
      toast.error('Delete failed');
    }
  }

  function handleStopDragEnd(draggedStopId, lat, lng) {
    const nextLat = round6(lat);
    const nextLng = round6(lng);
    const stop = stopsRef.current.find((item) => item._id === draggedStopId);

    if (!stop) {
      return;
    }

    if (editingStop?._id === draggedStopId) {
      setEditPendingMarker({ lat: nextLat, lng: nextLng });
      return;
    }

    setStops((previousStops) =>
      previousStops.map((item) =>
        item._id === draggedStopId ? { ...item, lat: nextLat, lng: nextLng } : item
      )
    );
    setDragPendingStop((previousPending) => ({
      id: draggedStopId,
      stopCode: stop.stopId,
      stopName: stop.stopName,
      origLat: previousPending?.id === draggedStopId ? previousPending.origLat : stop.lat,
      origLng: previousPending?.id === draggedStopId ? previousPending.origLng : stop.lng,
      newLat: nextLat,
      newLng: nextLng
    }));
  }

  async function handleSaveDraggedPosition() {
    if (!dragPendingStop) {
      return;
    }

    setSaving(true);

    try {
      await updateStop(dragPendingStop.id, {
        stopId: dragPendingStop.stopCode,
        stopName: dragPendingStop.stopName,
        lat: dragPendingStop.newLat,
        lng: dragPendingStop.newLng
      });

      toast.success('Position saved');
      setDragPendingStop(null);
      fetchStops();
    } catch {
      toast.error('Failed to save position');
      setStops((previousStops) =>
        previousStops.map((stop) =>
          stop._id === dragPendingStop.id
            ? { ...stop, lat: dragPendingStop.origLat, lng: dragPendingStop.origLng }
            : stop
        )
      );
      setDragPendingStop(null);
    }

    setSaving(false);
  }

  function cancelDraggedPosition() {
    if (!dragPendingStop) {
      return;
    }

    setStops((previousStops) =>
      previousStops.map((stop) =>
        stop._id === dragPendingStop.id
          ? { ...stop, lat: dragPendingStop.origLat, lng: dragPendingStop.origLng }
          : stop
      )
    );
    setDragPendingStop(null);
  }

  function switchTab(tab) {
    if (dragPendingStop) {
      updateStop(dragPendingStop.id, {
        stopId: dragPendingStop.stopCode,
        stopName: dragPendingStop.stopName,
        lat: dragPendingStop.newLat,
        lng: dragPendingStop.newLng
      }).catch(() => {
        setStops((previousStops) =>
          previousStops.map((stop) =>
            stop._id === dragPendingStop.id
              ? { ...stop, lat: dragPendingStop.origLat, lng: dragPendingStop.origLng }
              : stop
          )
        );
      });

      setDragPendingStop(null);
    }

    if (tab === 'stops') {
      setViewingRoute(null);
      setViewingRouteIds(new Set());
    } else if (tab !== 'routes') {
      setViewingRouteIds(new Set());
    }

    setActiveTab(tab);
    cancelEdit();
  }

  const stopSearchOptions = stopSearch.trim()
    ? stops
        .filter((stop) => {
          if (routeStops.some((routeStop) => routeStop._id === stop._id)) {
            return false;
          }
          const query = stopSearch.toLowerCase();
          return (
            stop.stopName.toLowerCase().includes(query) ||
            String(stop.stopId || '').toLowerCase().includes(query)
          );
        })
        .sort((left, right) => {
          const query = stopSearch.toLowerCase();
          const leftStartsWith =
            left.stopName.toLowerCase().startsWith(query) ||
            String(left.stopId || '').toLowerCase().startsWith(query);
          const rightStartsWith =
            right.stopName.toLowerCase().startsWith(query) ||
            String(right.stopId || '').toLowerCase().startsWith(query);

          if (leftStartsWith && !rightStartsWith) {
            return -1;
          }

          if (!leftStartsWith && rightStartsWith) {
            return 1;
          }

          return left.stopName.localeCompare(right.stopName);
        })
        .slice(0, 10)
    : [];

  function addStopToRoute(stop) {
    setRouteStops((previousStops) => [
      ...previousStops,
      {
        _id: stop._id,
        stopName: stop.stopName,
        lat: stop.lat,
        lng: stop.lng
      }
    ]);
    setStopSearch('');
    setFlyTarget({ lat: stop.lat, lng: stop.lng });
  }

  /*
   * Give a stop back to the stop master.
   *
   * Restores the master's current name and position for this route entry and drops
   * the route's own copy, so the stop follows the master again from here on.
   */
  function resetRouteStopToMaster(index) {
    const target = routeStops[index];
    const master = findStopMaster(target?._id);

    if (!master) {
      toast.error('This stop is no longer in the stop master');
      return;
    }

    setRouteStops((previousStops) =>
      previousStops.map((stop, currentIndex) =>
        currentIndex === index
          ? {
              ...stop,
              stopName: master.stopName,
              lat: master.lat,
              lng: master.lng,
              nameOverridden: false,
              positionOverridden: false
            }
          : stop
      )
    );
    setFlyTarget({ lat: master.lat, lng: master.lng });
  }

  // Whether a route stop currently differs from the stop master, and how.
  function describeStopOverride(stop) {
    const master = findStopMaster(stop?._id);
    if (!master) return { hasMaster: false, name: false, position: false };

    const name = String(stop.stopName ?? '').trim() !== String(master.stopName ?? '').trim();
    const position =
      Number(stop.lat).toFixed(6) !== Number(master.lat).toFixed(6) ||
      Number(stop.lng).toFixed(6) !== Number(master.lng).toFixed(6);

    return { hasMaster: true, name, position, masterName: master.stopName };
  }

  function removeStopFromRoute(index) {
    setRouteStops((previousStops) =>
      previousStops.filter((_, currentIndex) => currentIndex !== index)
    );
  }

  function moveStop(index, direction) {
    setRouteStops((previousStops) => {
      const nextStops = [...previousStops];
      const nextIndex = index + direction;

      if (nextIndex < 0 || nextIndex >= nextStops.length) {
        return previousStops;
      }

      [nextStops[index], nextStops[nextIndex]] = [
        nextStops[nextIndex],
        nextStops[index]
      ];

      return nextStops;
    });
  }

  function handleListDragStart(index) {
    dragItem.current = index;
  }

  function handleListDragEnter(index) {
    dragOverItem.current = index;
  }

  function handleListDragEnd() {
    const fromIndex = dragItem.current;
    const toIndex = dragOverItem.current;

    if (
      fromIndex === null ||
      toIndex === null ||
      fromIndex === toIndex
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    setRouteStops((previousStops) => {
      const nextStops = [...previousStops];
      const [draggedStop] = nextStops.splice(fromIndex, 1);
      nextStops.splice(toIndex, 0, draggedStop);
      return nextStops;
    });

    dragItem.current = null;
    dragOverItem.current = null;
  }

  /*
   * Repositioning a stop by dragging it on the Routes page.
   *
   * A stop's coordinates belong to the STOP MASTER, not to a route. A route only
   * embeds a copy of them for convenience, and the backend re-resolves that copy
   * from the master on both write (syncRouteStopsFromMaster) and read
   * (formatRouteForResponse). Writing a dragged position onto the route was
   * therefore discarded server-side: the request succeeded, the pin stayed put
   * because the local copy had been updated optimistically, and the old position
   * reappeared as soon as the page was reloaded.
   *
   * These handlers stage the move and commit it to the stop master instead. That is
   * also its correct meaning: one physical stop stands in one place, so moving it
   * moves it for every route that serves it. The pending banner says so before
   * anything is written.
   */

  /*
   * Repositioning a stop from inside a route.
   *
   * The new position belongs to THIS ROUTE only — it is saved onto the route, and
   * the stop master is left exactly as it was. Every other route that serves the
   * same stop is unaffected. Moving the stop itself is done from the Stops page,
   * which is the only place that writes to the stop master.
   *
   * The route save then records the divergence: the server compares each stop
   * against the master and marks the ones this route now owns.
   */

  // Drag while BUILDING or EDITING a route: the working copy is updated and the
  // change is written when the route itself is saved, alongside any reordering or
  // renaming done in the same sitting.
  function handleRouteStopDragEnd(index, lat, lng) {
    const nextLat = round6(lat);
    const nextLng = round6(lng);

    setRouteStops((previousStops) =>
      previousStops.map((stop, currentIndex) =>
        currentIndex === index
          ? { ...stop, lat: nextLat, lng: nextLng, positionOverridden: true }
          : stop
      )
    );
  }

  // Drag while VIEWING a saved route: there is no route form open to save from, so
  // the move is staged and written straight to that route on confirmation.
  function handleViewingRouteDragEnd(sequence, lat, lng) {
    if (!viewingRoute) {
      return;
    }

    const stop = viewingRoute.stops.find((item) => item.sequence === sequence);

    if (!stop) {
      return;
    }

    const nextLat = round6(lat);
    const nextLng = round6(lng);
    const stopRef = String(stop.stopId);

    setRouteDragPending((previous) => ({
      stopRef,
      stopName: resolveStopMasterName(stop),
      routeId: viewingRoute._id,
      // Keep the original position across successive drags of the same stop, so
      // Discard always returns to where it genuinely started.
      prevLat: previous?.stopRef === stopRef ? previous.prevLat : stop.lat,
      prevLng: previous?.stopRef === stopRef ? previous.prevLng : stop.lng,
      lat: nextLat,
      lng: nextLng
    }));

    const updatedStops = viewingRoute.stops.map((item) =>
      item.sequence === sequence
        ? { ...item, lat: nextLat, lng: nextLng, positionOverridden: true }
        : item
    );
    const updatedRoute = { ...viewingRoute, stops: updatedStops };

    setViewingRoute(updatedRoute);
    setSavedRoutes((previousRoutes) =>
      previousRoutes.map((route) =>
        route._id === viewingRoute._id ? updatedRoute : route
      )
    );
  }

  async function saveRouteDraggedPosition() {
    if (!routeDragPending || !viewingRoute) {
      return;
    }

    const pending = routeDragPending;
    setSaving(true);

    try {
      // Saved onto the ROUTE. The stop master is untouched — the server keeps this
      // position against this route only, and marks it as the route's own.
      await updateBusRoute(viewingRoute._id, {
        routeNumber: viewingRoute.routeNumber,
        direction: viewingRoute.direction,
        stops: [...viewingRoute.stops]
          .sort((left, right) => (left.sequence || 0) - (right.sequence || 0))
          .map((stop, index) => ({
            stopId: stop.stopId,
            stopName: resolveStopMasterName(stop),
            lat: stop.lat,
            lng: stop.lng,
            sequence: index + 1
          }))
      });

      toast.success(`"${pending.stopName}" moved on this route`);
      setRouteDragPending(null);
      await fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save position');
      cancelRouteDraggedPosition();
    }

    setSaving(false);
  }

  function cancelRouteDraggedPosition() {
    if (!routeDragPending) {
      return;
    }

    const { stopRef, prevLat, prevLng } = routeDragPending;

    // Snap the pin back in whichever surface it was dragged on.
    setRouteStops((previousStops) =>
      previousStops.map((stop) =>
        String(stop._id) === stopRef ? { ...stop, lat: prevLat, lng: prevLng } : stop
      )
    );

    if (viewingRoute) {
      const revertedStops = viewingRoute.stops.map((item) =>
        String(item.stopId) === stopRef ? { ...item, lat: prevLat, lng: prevLng } : item
      );
      const revertedRoute = { ...viewingRoute, stops: revertedStops };

      setViewingRoute(revertedRoute);
      setSavedRoutes((previousRoutes) =>
        previousRoutes.map((route) =>
          route._id === viewingRoute._id ? revertedRoute : route
        )
      );
    }

    setRouteDragPending(null);
  }

  function loadRouteForEdit(route) {
    setRouteDragPending(null);
    setEditingRoute(route);
    setRouteName(getRouteDisplayName(route, resolveStopMasterName));
    setRouteNumber(route.routeNumber);
    setRouteDirection(route.direction);

    const sortedStops = [...route.stops].sort(
      (left, right) => left.sequence - right.sequence
    );

    // Carries this route's own values, plus the flags saying which of them the
    // route owns rather than inherits from the stop master.
    setRouteStops(
      sortedStops.map((stop) => ({
        _id: stop.stopId,
        stopName: resolveStopMasterName(stop),
        lat: stop.lat,
        lng: stop.lng,
        nameOverridden: !!stop.nameOverridden,
        positionOverridden: !!stop.positionOverridden
      }))
    );
    setViewingRoute(null);
    setViewingRouteIds(new Set());

    if (sortedStops.length > 0) {
      setFlyTarget({ lat: sortedStops[0].lat, lng: sortedStops[0].lng });
    }
  }

  function cancelRouteEdit() {
    setEditingRoute(null);
    setRouteName('');
    setRouteNumber('');
    setRouteDirection('');
    setRouteStops([]);
  }

  const derivedRouteName = useMemo(() => {
    if (routeStops.length < 2) {
      return '';
    }
    const first = routeStops[0]?.stopName?.trim() || '';
    const last = routeStops[routeStops.length - 1]?.stopName?.trim() || '';
    return first && last ? `${first} - ${last}` : '';
  }, [routeStops]);

  function requestSaveRoute() {
    if (!routeNumber.trim()) {
      toast.error('Enter a route number');
      return;
    }

    if (!routeDirection.trim()) {
      toast.error('Enter a direction');
      return;
    }

    if (routeStops.length < 2) {
      toast.error('Add at least 2 stops');
      return;
    }

    setRouteSaveConfirm({
      routeName: derivedRouteName,
      routeNumber: routeNumber.trim(),
      direction: routeDirection.trim(),
      stopCount: routeStops.length,
      isUpdate: !!editingRoute
    });
  }

  function cancelSaveRouteConfirm() {
    setRouteSaveConfirm(null);
  }

  async function handleSaveRoute() {
    if (!routeSaveConfirm) {
      requestSaveRoute();
      return;
    }

    setSaving(true);

    const payload = {
      routeName: derivedRouteName,
      routeNumber: routeNumber.trim(),
      direction: routeDirection.trim(),
      stops: routeStops.map((stop, index) => ({
        stopId: stop._id,
        stopName: stop.stopName,
        lat: stop.lat,
        lng: stop.lng,
        sequence: index + 1
      }))
    };

    try {
      if (editingRoute) {
        await updateBusRoute(editingRoute._id, payload);
        toast.success(`Route "${derivedRouteName}" updated`);
      } else {
        await createBusRoute(payload);
        toast.success(`Route "${derivedRouteName}" created`);
      }

      setEditingRoute(null);
      setRouteName('');
      setRouteNumber('');
      setRouteDirection('');
      setRouteStops([]);
      setViewingRoute(null);
      setViewingRouteIds(new Set());
      setRouteDragPending(null);
      setRouteSaveConfirm(null);
      fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save route');
    }

    setSaving(false);
  }

  async function handleDeleteRoute(id, name) {
    if (!window.confirm(`Delete route "${name}"?`)) {
      return;
    }

    try {
      await deleteBusRoute(id);
      removeRoutesFromState([id]);
      toast.success('Route deleted');
    } catch {
      toast.error('Delete failed');
    }
  }

  function removeRoutesFromState(ids) {
    const idsToRemove = new Set(ids);

    setSavedRoutes((previousRoutes) =>
      previousRoutes.filter((route) => !idsToRemove.has(route._id))
    );
    setSelectedDataRouteIds(
      (previousIds) => new Set([...previousIds].filter((id) => !idsToRemove.has(id)))
    );

    if (viewingRoute?._id && idsToRemove.has(viewingRoute._id)) {
      setViewingRoute(null);
      setRouteDragPending(null);
    }

    setViewingRouteIds((previousIds) => {
      const nextIds = new Set([...previousIds].filter((id) => !idsToRemove.has(id)));
      return nextIds.size === previousIds.size ? previousIds : nextIds;
    });

    if (editingRoute?._id && idsToRemove.has(editingRoute._id)) {
      cancelRouteEdit();
    }
  }

  async function parseRouteFileForPreview(file, masterByStopId) {
    const xlsx = await import('xlsx');
    const buffer = await file.arrayBuffer();
    const workbook = xlsx.read(buffer, { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const rawRows = xlsx.utils.sheet_to_json(worksheet, { defval: '' });

    if (rawRows.length === 0) {
      return { fileName: file.name, error: 'File is empty or has no data rows.' };
    }

    const headers = Object.keys(rawRows[0]);
    const normalizedHeaders = headers.map((header) => ({
      raw: header,
      normalized: normalizeImportHeader(header)
    }));

    const sequenceCol = normalizedHeaders.find((header) =>
      ['sequenceno', 'sequencenumber', 'sequence', 'seq'].includes(header.normalized)
    )?.raw;
    const directionCol = normalizedHeaders.find((header) =>
      ['direction', 'directions', 'routedirection'].includes(header.normalized)
    )?.raw;
    const stopIdCol = normalizedHeaders.find(
      (header) => header.normalized === 'stopid'
    )?.raw;
    const routeNumberCol = normalizedHeaders.find((header) =>
      ['routenumber', 'routeno'].includes(header.normalized)
    )?.raw;

    if (!sequenceCol || !directionCol || !stopIdCol || !routeNumberCol) {
      return {
        fileName: file.name,
        error: 'Required columns: SequenceNo, StopID, Direction, RouteNo'
      };
    }

    const rows = [];
    let skipped = 0;

    rawRows.forEach((row) => {
      const sequence = parseInt(row[sequenceCol], 10);
      const stopId = String(row[stopIdCol] ?? '').trim();
      const directionRaw = String(row[directionCol] ?? '').trim();
      const direction =
        directionRaw === '1' ? 'Up' : directionRaw === '0' ? 'Down' : directionRaw;
      const routeNumberRaw = String(row[routeNumberCol] ?? '').trim();
      const routeNumberMatch = routeNumberRaw.match(/\d+/);
      const routeNumber = routeNumberMatch ? routeNumberMatch[0] : '';

      if (Number.isInteger(sequence) && stopId && direction && routeNumber) {
        rows.push({ sequence, stopId, direction, routeNumber });
      } else {
        skipped += 1;
      }
    });

    if (rows.length < 2) {
      return { fileName: file.name, error: 'Need at least 2 valid rows in the file.' };
    }

    const directions = [...new Set(rows.map((r) => r.direction))];
    if (directions.length !== 1) {
      return {
        fileName: file.name,
        error: 'Each file must contain exactly one Direction.'
      };
    }

    const routeNumbers = [...new Set(rows.map((r) => r.routeNumber))];
    if (routeNumbers.length !== 1) {
      return {
        fileName: file.name,
        error: 'Each file must contain exactly one RouteNo.'
      };
    }

    rows.sort((a, b) => a.sequence - b.sequence);

    const seenIds = new Set();
    const missingStopIds = [];
    const resolvedStops = rows.map((row) => {
      const master = masterByStopId.get(row.stopId);
      if (!master && !seenIds.has(row.stopId)) {
        missingStopIds.push(row.stopId);
        seenIds.add(row.stopId);
      }
      return {
        sequence: row.sequence,
        stopId: row.stopId,
        stopName: master?.stopName || '',
        lat: master?.lat ?? null,
        lng: master?.lng ?? null,
        masterId: master?._id || null
      };
    });

    const derivedName =
      missingStopIds.length === 0
        ? `${resolvedStops[0].stopName} - ${resolvedStops[resolvedStops.length - 1].stopName}`
        : '';

    return {
      fileName: file.name,
      routeNumber: routeNumbers[0],
      direction: directions[0],
      routeName: derivedName,
      stops: resolvedStops,
      missingStopIds,
      skipped
    };
  }

  async function reparseSelectedRouteFiles(filesToParse, currentStops) {
    if (!filesToParse.length) {
      return;
    }

    const masterByStopId = new Map(
      currentStops.map((stop) => [String(stop.stopId), stop])
    );

    setRouteImportParsing(true);

    try {
      const previews = await Promise.all(
        filesToParse.map((file) =>
          parseRouteFileForPreview(file, masterByStopId)
        )
      );
      setRouteImportPreviews(previews);

      const aggregatedMissing = [
        ...new Set(
          previews.flatMap((preview) => preview.missingStopIds || [])
        )
      ];
      setPendingMissingStopIds(aggregatedMissing);
    } finally {
      setRouteImportParsing(false);
    }
  }

  const routeImportFilesRef = useRef([]);
  const routeImportPreviewsRef = useRef([]);

  useEffect(() => {
    routeImportFilesRef.current = routeImportFiles;
  }, [routeImportFiles]);

  useEffect(() => {
    routeImportPreviewsRef.current = routeImportPreviews;
  }, [routeImportPreviews]);

  useEffect(() => {
    if (
      routeImportFilesRef.current.length > 0 &&
      routeImportPreviewsRef.current.length > 0
    ) {
      reparseSelectedRouteFiles(routeImportFilesRef.current, stops).catch(() => {
        // ignore transient parse failures during re-resolution
      });
    }
  }, [stops]);

  async function handleRouteImportFileChange(event) {
    const files = Array.from(event.target.files || []);
    setRouteImportFiles(files);
    setRouteImportPreviews([]);

    if (files.length === 0) {
      return;
    }

    const masterByStopId = new Map(
      stops.map((stop) => [String(stop.stopId), stop])
    );

    setRouteImportParsing(true);

    try {
      const previews = await Promise.all(
        files.map((file) => parseRouteFileForPreview(file, masterByStopId))
      );
      setRouteImportPreviews(previews);

      const aggregatedMissing = [
        ...new Set(
          previews.flatMap((preview) => preview.missingStopIds || [])
        )
      ];
      setPendingMissingStopIds(aggregatedMissing);
    } catch (error) {
      toast.error(`Failed to parse file: ${error.message}`);
    } finally {
      setRouteImportParsing(false);
    }
  }

  async function handleRouteImport() {
    if (routeImportFiles.length === 0) {
      toast.error('Select one or more route files');
      return;
    }

    if (routeImportPreviews.some((preview) => preview.error)) {
      toast.error('Resolve file errors before importing');
      return;
    }

    if (routeImportPreviews.some((preview) => (preview.missingStopIds || []).length > 0)) {
      toast.error('Some StopIDs are missing from StopMaster. Create them first.');
      return;
    }

    const formData = new FormData();
    routeImportFiles.forEach((file) => formData.append('files', file));

    setRouteImporting(true);

    try {
      const response = await importBusRoutes(formData);
      const imported = response.data.imported || [];
      const errors = response.data.errors || [];

      if (imported.length > 0) {
        const firstRoute = imported[0];
        setViewingRoute(firstRoute);

        const sortedStops = [...firstRoute.stops].sort(
          (left, right) => left.sequence - right.sequence
        );

        if (sortedStops.length > 0) {
          setFlyTarget({ lat: sortedStops[0].lat, lng: sortedStops[0].lng });
        }
      }

      const aggregatedMissing = [
        ...new Set(errors.flatMap((item) => item.missingStopIds || []))
      ];

      if (aggregatedMissing.length > 0) {
        setPendingMissingStopIds(aggregatedMissing);
      }

      if (errors.length > 0) {
        toast.warn(`${imported.length} route(s) imported, ${errors.length} file(s) failed`);
      } else {
        toast.success(`${imported.length} route(s) imported`);
        setRouteImportFiles([]);
        setRouteImportPreviews([]);
        setPendingMissingStopIds([]);

        if (routeImportInputRef.current) {
          routeImportInputRef.current.value = '';
        }
      }

      fetchRoutes();
    } catch (error) {
      const apiError = error.response?.data;
      const details = apiError?.errors
        ?.map((item) => `${item.fileName}: ${item.error}`)
        .join(' | ');

      const aggregatedMissing = [
        ...new Set(
          (apiError?.errors || []).flatMap((item) => item.missingStopIds || [])
        )
      ];

      if (aggregatedMissing.length > 0) {
        setPendingMissingStopIds(aggregatedMissing);
      }

      toast.error(details || apiError?.error || 'Route import failed');
    }

    setRouteImporting(false);
  }

  function clearSelectedRouteFiles() {
    setRouteImportFiles([]);
    setRouteImportPreviews([]);
    setPendingMissingStopIds([]);

    if (routeImportInputRef.current) {
      routeImportInputRef.current.value = '';
    }
  }

  function jumpToCreateStop(stopIdValue) {
    setStopId(stopIdValue || '');
    setStopName('');
    setPendingMarker(null);
    setSearchQuery('');
    setCoordsInput('');
    setViewingRoute(null);
    setViewingRouteIds(new Set());
    cancelEdit();
    setStopFormOpen(true);
    setActiveTab('stops');
  }

  function toggleViewRoute(route) {
    if (viewingRoute?._id === route._id) {
      cancelRouteDraggedPosition();
      setViewingRoute(null);
      setViewingRouteIds(new Set());
      setRouteDragPending(null);
      return;
    }

    if (routeDragPending) {
      cancelRouteDraggedPosition();
    }

    setViewingRoute(route);
    setViewingRouteIds(new Set());
    setRouteDragPending(null);

    const sortedStops = [...route.stops].sort(
      (left, right) => left.sequence - right.sequence
    );

    if (sortedStops.length > 0) {
      setFlyTarget({ lat: sortedStops[0].lat, lng: sortedStops[0].lng });
    }
  }

  function viewStopsOnMap(ids) {
    const idArray = Array.isArray(ids) ? ids : [...ids];
    if (idArray.length === 0) return;

    setVisibleStopIds(new Set(idArray));
    setViewingRoute(null);
    setViewingRouteIds(new Set());
    cancelEdit();
    setStopFormOpen(false);
    setActiveTab('map');

    const first = stopsRef.current.find((stop) => stop._id === idArray[0]);
    if (first) {
      setFlyTarget({ lat: first.lat, lng: first.lng });
    }
  }

  function clearMapSelection() {
    setVisibleStopIds(new Set());
    setViewingRoute(null);
    setViewingRouteIds(new Set());
    cancelEdit();
    cancelRouteDraggedPosition();
    setStopFormOpen(false);
    setPendingMarker(null);
    setStopId('');
    setStopName('');
    setSearchQuery('');
    setCoordsInput('');
  }

  function openCreateStopForm() {
    cancelEdit();
    setPendingMarker(null);
    setStopId('');
    setStopName('');
    setSearchQuery('');
    setCoordsInput('');
    setViewingRoute(null);
    setViewingRouteIds(new Set());
    setStopFormOpen(true);
    setActiveTab('stops');
  }

  function closeStopForm() {
    setStopFormOpen(false);
    cancelEdit();
    setPendingMarker(null);
    setStopId('');
    setStopName('');
    setSearchQuery('');
    setCoordsInput('');
  }

  function toggleSidebar() {
    setSidebarCollapsed((previous) => {
      const next = !previous;
      try {
        localStorage.setItem('app.sidebar.collapsed', next ? '1' : '0');
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }

  function viewRoutesOnMap(routes) {
    if (!routes || routes.length === 0) return;

    const [primary, ...rest] = routes;

    if (routeDragPending) {
      cancelRouteDraggedPosition();
    }

    cancelRouteEdit();
    setViewingRoute(primary);
    setViewingRouteIds(new Set(rest.map((route) => route._id)));
    setActiveTab('routes');

    const sortedStops = [...primary.stops].sort(
      (left, right) => left.sequence - right.sequence
    );
    if (sortedStops.length > 0) {
      setFlyTarget({ lat: sortedStops[0].lat, lng: sortedStops[0].lng });
    }
  }

  // Make one of the currently-loaded routes the focused (bold) route without
  // dropping the rest of the multi-route selection from the map.
  function focusRoute(route) {
    if (!route) return;

    if (routeDragPending) {
      cancelRouteDraggedPosition();
    }

    const remainingIds = new Set();
    if (viewingRoute && viewingRoute._id !== route._id) {
      remainingIds.add(viewingRoute._id);
    }
    viewingRouteIds.forEach((id) => {
      if (id !== route._id) remainingIds.add(id);
    });

    setViewingRoute(route);
    setViewingRouteIds(remainingIds);

    const sortedStops = [...route.stops].sort(
      (left, right) => left.sequence - right.sequence
    );
    if (sortedStops.length > 0) {
      setFlyTarget({ lat: sortedStops[0].lat, lng: sortedStops[0].lng });
    }
  }

  async function handleFileChange(event) {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    setImportRows([]);
    setImportError('');
    setImportSkipped(0);

    try {
      const xlsx = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const workbook = xlsx.read(buffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawRows = xlsx.utils.sheet_to_json(worksheet, { defval: '' });

      if (rawRows.length === 0) {
        setImportError('File is empty or has no data rows.');
        return;
      }

      const headers = Object.keys(rawRows[0]);
      const normalizedHeaders = headers.map((header) => ({
        raw: header,
        normalized: normalizeImportHeader(header)
      }));

      const stopIdColumn = normalizedHeaders.find(
        (header) => header.normalized === 'stopid'
      )?.raw;
      const stopNameColumn =
        normalizedHeaders.find((header) =>
          ['stopname', 'name', 'stopfullname', 'stopmastername'].includes(
            header.normalized
          )
        )?.raw ||
        normalizedHeaders.find(
          (header) =>
            header.normalized !== 'stopid' &&
            header.normalized.includes('name')
        )?.raw;
      const latitudeColumn = headers.find((header) => /^lat/i.test(header));
      const longitudeColumn = headers.find((header) => /^(lng|lon|long)/i.test(header));

      // StopID is OPTIONAL — when the column is absent or a cell is blank, the
      // server assigns the id. Only name + coordinates are required.
      if (!stopNameColumn || !latitudeColumn || !longitudeColumn) {
        setImportError(
          `Could not detect columns. Found: "${headers.join('", "')}". Need: name/stop, lat, lng/long (StopID optional).`
        );
        return;
      }

      const validRows = [];
      let skippedRows = 0;

      rawRows.forEach((row) => {
        const currentStopId = stopIdColumn ? String(row[stopIdColumn]).trim() : '';
        const currentStopName = String(row[stopNameColumn]).trim();
        const lat = round6(parseFloat(row[latitudeColumn]));
        const lng = round6(parseFloat(row[longitudeColumn]));

        if (currentStopName && !Number.isNaN(lat) && !Number.isNaN(lng)) {
          const parsed = { stopName: currentStopName, lat, lng };
          if (currentStopId) parsed.stopId = currentStopId;
          validRows.push(parsed);
        } else {
          skippedRows += 1;
        }
      });

      // Dedupe by StopID when present; otherwise by name + coordinates so the
      // same sheet row isn't imported twice.
      const dedupedRows = Array.from(
        validRows
          .reduce((map, row) => {
            const key = row.stopId
              ? `id:${row.stopId}`
              : `nc:${row.stopName.toLowerCase()}|${row.lat}|${row.lng}`;
            return map.set(key, row);
          }, new Map())
          .values()
      );
      skippedRows += validRows.length - dedupedRows.length;

      if (dedupedRows.length === 0) {
        setImportError('No valid rows found after parsing.');
        return;
      }

      setImportRows(dedupedRows);
      setImportSkipped(skippedRows);
    } catch (error) {
      setImportError(`Failed to parse file: ${error.message}`);
    }
  }

  async function handleImport() {
    if (importRows.length === 0) {
      return;
    }

    setImporting(true);

    try {
      const response = await bulkCreateStops(importRows);
      const processed = response.data.processed ?? importRows.length;
      const detail = [];

      if (response.data.inserted) {
        detail.push(`${response.data.inserted} new`);
      }

      if (response.data.updated) {
        detail.push(`${response.data.updated} updated`);
      }

      toast.success(
        detail.length > 0
          ? `${processed} stops synced (${detail.join(', ')})`
          : `${processed} stops synced`
      );
      setImportRows([]);
      setImportSkipped(0);
      setImportError('');

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      fetchStops(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Import failed');
    }

    setImporting(false);
  }

  const stopMasterNameMap = useMemo(() => {
    const nextMap = new Map();

    stops.forEach((stop) => {
      nextMap.set(String(stop._id), stop.stopName);
      nextMap.set(String(stop.stopId), stop.stopName);
    });

    return nextMap;
  }, [stops]);

  /*
   * The name to show for a stop as it appears ON A ROUTE.
   *
   * A route that carries its own name for the stop keeps it — only routes saved
   * before renaming moved to Stop Master can. Every other stop shows the stop
   * master's current name, so a correction there reaches all of them.
   */
  function resolveStopMasterName(stopReference) {
    if (!stopReference) {
      return '';
    }

    if (stopReference.nameOverridden && stopReference.stopName) {
      return stopReference.stopName;
    }

    const masterName = stopMasterNameMap.get(
      String(stopReference.stopId ?? stopReference._id ?? '')
    );

    return masterName || stopReference.stopName || '';
  }

  // The stop master record behind a route stop, for comparing against overrides.
  function findStopMaster(stopReference) {
    const raw = String(stopReference ?? '');
    return (
      stopsRef.current.find((master) => String(master._id) === raw) ||
      stopsRef.current.find((master) => String(master.stopId) === raw) ||
      null
    );
  }

  const mapRouteStops = viewingRoute
    ? [...viewingRoute.stops]
        .sort((left, right) => left.sequence - right.sequence)
        .map((stop) => ({ ...stop, stopName: resolveStopMasterName(stop) }))
    : routeStops.map((stop, index) => ({
        stopId: stop._id,
        stopName: stop.stopName,
        lat: stop.lat,
        lng: stop.lng,
        sequence: index + 1
      }));

  const viewingRouteStops = viewingRoute
    ? [...viewingRoute.stops]
        .sort((left, right) => left.sequence - right.sequence)
        .map((stop) => ({ ...stop, stopName: resolveStopMasterName(stop) }))
    : [];

  // Every route currently loaded on the map (focused + others), ordered by the
  // saved-routes order so colors stay stable no matter which one is focused.
  const selectedRouteList = useMemo(() => {
    if (!viewingRoute && viewingRouteIds.size === 0) return [];

    const ids = new Set(viewingRouteIds);
    if (viewingRoute) ids.add(viewingRoute._id);

    return savedRoutes.filter((route) => ids.has(route._id));
  }, [savedRoutes, viewingRoute, viewingRouteIds]);

  const routeColorMap = useMemo(() => {
    const map = new Map();
    selectedRouteList.forEach((route, index) => {
      map.set(route._id, routePaletteColor(index));
    });
    return map;
  }, [selectedRouteList]);

  const stopsOnMap = stops.filter(
    (stop) => visibleStopIds.has(stop._id) || editingStop?._id === stop._id
  );
  const editingIdx = editingStop
    ? stops.findIndex((stop) => stop._id === editingStop._id)
    : -1;

  const panelFilteredStops = useMemo(() => {
    if (!panelSearch.trim()) {
      return stops;
    }

    const query = panelSearch.toLowerCase();

    return stops.filter(
      (stop) =>
        stop.stopName.toLowerCase().includes(query) ||
        String(stop.stopId || '').toLowerCase().includes(query)
    );
  }, [panelSearch, stops]);

  const mapStopSearchResults = useMemo(() => {
    if (!mapStopSearch.trim()) {
      return [];
    }

    const query = mapStopSearch.toLowerCase();

    return stops
      .filter(
        (stop) =>
          stop.stopName.toLowerCase().includes(query) ||
          String(stop.stopId || '').toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [mapStopSearch, stops]);

  const filteredDataStops = useMemo(() => {
    let nextStops = [...stops];

    if (dataStopsFilter.trim()) {
      const query = dataStopsFilter.toLowerCase();
      nextStops = nextStops.filter(
        (stop) =>
          stop.stopName.toLowerCase().includes(query) ||
          String(stop.stopId || '').toLowerCase().includes(query)
      );
    }

    nextStops.sort((left, right) => {
      const { dir, key } = dataStopsSort;

      if (key === 'stopId') {
        return dir * String(left.stopId || '').localeCompare(String(right.stopId || ''));
      }

      if (key === 'stopName') {
        return dir * left.stopName.localeCompare(right.stopName);
      }

      if (key === 'lat') {
        return dir * (left.lat - right.lat);
      }

      if (key === 'lng') {
        return dir * (left.lng - right.lng);
      }

      if (key === 'createdAt') {
        return dir * (new Date(left.createdAt) - new Date(right.createdAt));
      }

      return 0;
    });

    return nextStops;
  }, [dataStopsFilter, dataStopsSort, stops]);

  const filteredDataRoutes = useMemo(() => {
    let nextRoutes = [...savedRoutes];

    if (dataRoutesFilter.trim()) {
      const query = dataRoutesFilter.toLowerCase();
      nextRoutes = nextRoutes.filter(
        (route) =>
          getRouteDisplayName(route, resolveStopMasterName).toLowerCase().includes(query) ||
          route.routeNumber.toLowerCase().includes(query) ||
          route.direction.toLowerCase().includes(query)
      );
    }

    nextRoutes.sort((left, right) => {
      const { dir, key } = dataRoutesSort;

      if (key === 'routeName') {
        return (
          dir *
          getRouteDisplayName(left, resolveStopMasterName).localeCompare(
            getRouteDisplayName(right, resolveStopMasterName)
          )
        );
      }

      if (key === 'routeNumber') {
        return dir * left.routeNumber.localeCompare(right.routeNumber);
      }

      if (key === 'direction') {
        return dir * left.direction.localeCompare(right.direction);
      }

      if (key === 'stops') {
        return dir * (left.stops.length - right.stops.length);
      }

      if (key === 'createdAt') {
        return dir * (new Date(left.createdAt) - new Date(right.createdAt));
      }

      return 0;
    });

    return nextRoutes;
  }, [dataRoutesFilter, dataRoutesSort, resolveStopMasterName, savedRoutes]);

  const dataStopsTotalPages = Math.max(
    1,
    Math.ceil(filteredDataStops.length / DATA_PAGE_SIZE)
  );
  const dataRoutesTotalPages = Math.max(
    1,
    Math.ceil(filteredDataRoutes.length / DATA_PAGE_SIZE)
  );

  useEffect(() => {
    setDataStopsPage((previousPage) => Math.min(previousPage, dataStopsTotalPages));
  }, [dataStopsTotalPages]);

  useEffect(() => {
    setDataRoutesPage((previousPage) => Math.min(previousPage, dataRoutesTotalPages));
  }, [dataRoutesTotalPages]);

  const dataStopsPageItems = filteredDataStops.slice(
    (dataStopsPage - 1) * DATA_PAGE_SIZE,
    dataStopsPage * DATA_PAGE_SIZE
  );
  const dataRoutesPageItems = filteredDataRoutes.slice(
    (dataRoutesPage - 1) * DATA_PAGE_SIZE,
    dataRoutesPage * DATA_PAGE_SIZE
  );
  const selectedFilteredStopsCount = filteredDataStops.filter((stop) =>
    selectedDataStopIds.has(stop._id)
  ).length;
  const selectedFilteredRoutesCount = filteredDataRoutes.filter((route) =>
    selectedDataRouteIds.has(route._id)
  ).length;
  const allStopsOnPageSelected =
    dataStopsPageItems.length > 0 &&
    dataStopsPageItems.every((stop) => selectedDataStopIds.has(stop._id));
  const allRoutesOnPageSelected =
    dataRoutesPageItems.length > 0 &&
    dataRoutesPageItems.every((route) => selectedDataRouteIds.has(route._id));

  function toggleDataSelection(type, id) {
    const setSelection =
      type === 'stops' ? setSelectedDataStopIds : setSelectedDataRouteIds;

    setSelection((previousSelection) => {
      const nextSelection = new Set(previousSelection);

      if (nextSelection.has(id)) {
        nextSelection.delete(id);
      } else {
        nextSelection.add(id);
      }

      return nextSelection;
    });
  }

  function selectDataItems(type, ids) {
    const setSelection =
      type === 'stops' ? setSelectedDataStopIds : setSelectedDataRouteIds;

    setSelection((previousSelection) => {
      const nextSelection = new Set(previousSelection);
      ids.forEach((id) => nextSelection.add(id));
      return nextSelection;
    });
  }

  function deselectDataItems(type, ids) {
    const setSelection =
      type === 'stops' ? setSelectedDataStopIds : setSelectedDataRouteIds;

    setSelection((previousSelection) => {
      const nextSelection = new Set(previousSelection);
      ids.forEach((id) => nextSelection.delete(id));
      return nextSelection;
    });
  }

  async function handleBulkDeleteStops() {
    const ids = [...selectedDataStopIds];

    if (ids.length === 0) {
      return;
    }

    if (!window.confirm(`Delete ${ids.length} selected stop${ids.length === 1 ? '' : 's'}?`)) {
      return;
    }

    setDeletingSelection(true);

    try {
      await bulkDeleteStops(ids);
      removeStopsFromState(ids);
      toast.success(`${ids.length} stop${ids.length === 1 ? '' : 's'} deleted`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Bulk delete failed');
    }

    setDeletingSelection(false);
  }

  async function handleBulkDeleteRoutes() {
    const ids = [...selectedDataRouteIds];

    if (ids.length === 0) {
      return;
    }

    if (!window.confirm(`Delete ${ids.length} selected route${ids.length === 1 ? '' : 's'}?`)) {
      return;
    }

    setDeletingSelection(true);

    try {
      await bulkDeleteBusRoutes(ids);
      removeRoutesFromState(ids);
      toast.success(`${ids.length} route${ids.length === 1 ? '' : 's'} deleted`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Bulk delete failed');
    }

    setDeletingSelection(false);
  }

  function toggleDataSort(table, key) {
    if (table === 'stops') {
      setDataStopsSort((previousSort) =>
        previousSort.key === key ? { key, dir: -previousSort.dir } : { key, dir: 1 }
      );
      setDataStopsPage(1);
      return;
    }

    setDataRoutesSort((previousSort) =>
      previousSort.key === key ? { key, dir: -previousSort.dir } : { key, dir: 1 }
    );
    setDataRoutesPage(1);
  }

  return {
    activeTab,
    addStopToRoute,
    allRoutesOnPageSelected,
    allStopsOnPageSelected,
    cancelDraggedPosition,
    cancelEdit,
    cancelRouteDraggedPosition,
    cancelRouteEdit,
    clearSelectedRouteFiles,
    coordsInput,
    dataRoutesFilter,
    dataRoutesPage,
    dataRoutesPageItems,
    dataRoutesSort,
    dataRoutesTotalPages,
    dataStopsFilter,
    dataStopsPage,
    dataStopsPageItems,
    dataStopsSort,
    dataStopsTotalPages,
    dataSubTab,
    deletingSelection,
    deselectDataItems,
    dragPendingStop,
    editingIdx,
    editingRoute,
    editingStop,
    editName,
    editPendingMarker,
    editStopId,
    fileInputRef,
    filteredDataRoutes,
    filteredDataStops,
    flyTarget,
    handleBulkDeleteRoutes,
    handleBulkDeleteStops,
    handleCoordsInput,
    handleDeleteRoute,
    handleDeleteStop,
    handleFileChange,
    handleImport,
    handleListDragEnd,
    handleListDragEnter,
    handleListDragStart,
    handleMapClick,
    handleRouteImport,
    handleRouteImportFileChange,
    handleRouteStopDragEnd,
    handleSave,
    handleSaveDraggedPosition,
    handleSaveRoute,
    handleSearchInput,
    handleSelectResult,
    handleStopDragEnd,
    handleUpdate,
    requestUpdateStop,
    stopUpdateConfirm,
    cancelStopUpdateConfirm,
    handleViewingRouteDragEnd,
    importError,
    importing,
    importRows,
    importSkipped,
    isDraggingMarker,
    loadRouteForEdit,
    mapRouteStops,
    mapSearchFocused,
    mapSearchRef,
    mapStopSearch,
    mapStopSearchResults,
    moveStop,
    panelFilteredStops,
    panelSearch,
    pendingMarker,
    removeStopFromRoute,
    // Per-route stop overrides — read-only now except for the reset, which hands a
    // stop that diverged earlier back to the stop master.
    resetRouteStopToMaster,
    describeStopOverride,
    resolveStopMasterName,
    routeBuilderMode,
    routeDirection,
    routeDragPending,
    routeImportFiles,
    routeImporting,
    routeImportInputRef,
    routeImportParsing,
    routeImportPreviews,
    routeName,
    derivedRouteName,
    routeSaveConfirm,
    requestSaveRoute,
    cancelSaveRouteConfirm,
    pendingMissingStopIds,
    jumpToCreateStop,
    routeNumber,
    routeSearchRef,
    routeSearchOptions: stopSearchOptions,
    routeStops,
    saveRouteDraggedPosition,
    savedRoutes,
    saving,
    searchQuery,
    searchRef,
    searchResults,
    selectDataItems,
    selectedDataRouteIds,
    selectedDataStopIds,
    selectedFilteredRoutesCount,
    selectedFilteredStopsCount,
    setDataRoutesFilter,
    setDataRoutesPage,
    setDataSubTab,
    setDataStopsFilter,
    setDataStopsPage,
    setEditName,
    setEditStopId,
    setFlyTarget,
    setMapSearchFocused,
    setMapStopSearch,
    setPanelSearch,
    setRouteBuilderMode,
    setRouteDirection,
    setRouteName,
    setRouteNumber,
    setSelectedDataRouteIds,
    setSelectedDataStopIds,
    setStopId,
    setStopName,
    setStopSearch,
    setViewingRoute,
    setVisibleStopIds,
    startEdit,
    stopId,
    stopName,
    stopSearch,
    stops,
    stopsOnMap,
    switchTab,
    toggleDataSelection,
    toggleDataSort,
    toggleStopVisibility,
    toggleViewRoute,
    focusRoute,
    selectedRouteList,
    routeColorMap,
    viewingRoute,
    viewingRouteIds,
    viewingRouteStops,
    viewRoutesOnMap,
    viewStopsOnMap,
    visibleStopIds,
    clearMapSelection,
    closeStopForm,
    openCreateStopForm,
    sidebarCollapsed,
    stopFormOpen,
    toggleSidebar
  };
}
